// The scene's colours come from the page: the fire from the design tokens,
// the sky and land from the same theme variables the CSS backdrop paints
// (styles/theme.css), so the poster and the live scene are one picture.
// Read at start-up and again whenever the theme changes.

export type Linear = [number, number, number]

export interface ScenePalette {
  day: boolean
  gold: Linear
  goldHi: Linear
  goldDeep: Linear
  ember: Linear
  emberHot: Linear
  emberDeep: Linear
  star: Linear
  skyTop: Linear
  skyMid: Linear
  skyLow: Linear
  /** The low glow (premultiplied by its alpha). */
  sun: Linear
  ridgeFar: Linear
  ridgeMid: Linear
  ridgeNear: Linear
  fire: Linear
}

/** "201 180 88" (a token's RGB triplet) to 0-1 sRGB; null for anything else. */
export function parseTriplet(value: string): Linear | null {
  const parts = value.trim().split(/[\s,]+/).map(Number)
  if (parts.length < 3 || parts.slice(0, 3).some(part => !Number.isFinite(part) || part < 0 || part > 255)) return null
  return [parts[0] / 255, parts[1] / 255, parts[2] / 255]
}

/** sRGB to linear light, per channel. */
export function toLinear([r, g, b]: Linear): Linear {
  const channel = (c: number) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4))
  return [channel(r), channel(g), channel(b)]
}

const GOLD: Linear = [0.79, 0.71, 0.35]

/** Resolves any CSS colour (color-mix included) to sRGB through a 1 x 1 canvas. */
function colourResolver(): (css: string) => { rgb: Linear; alpha: number } | null {
  const canvas = document.createElement('canvas')
  canvas.width = 1
  canvas.height = 1
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  return css => {
    if (!ctx || !css.trim()) return null
    ctx.clearRect(0, 0, 1, 1)
    ctx.fillStyle = '#000'
    ctx.fillStyle = css
    ctx.fillRect(0, 0, 1, 1)
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data
    return { rgb: [r / 255, g / 255, b / 255], alpha: a / 255 }
  }
}

export function readPalette(root: HTMLElement = document.documentElement): ScenePalette {
  const style = getComputedStyle(root)
  const resolve = colourResolver()
  const token = (name: string, fallback: Linear = GOLD) => toLinear(parseTriplet(style.getPropertyValue(name)) ?? fallback)
  const themed = (name: string, fallback: Linear, premultiply = false): Linear => {
    const value = resolve(style.getPropertyValue(name))
    if (!value) return toLinear(fallback)
    const linear = toLinear(value.rgb)
    return premultiply ? [linear[0] * value.alpha, linear[1] * value.alpha, linear[2] * value.alpha] : linear
  }
  return {
    day: !root.classList.contains('dark'),
    gold: token('--color-primary'),
    goldHi: token('--color-primary-light', [0.91, 0.87, 0.69]),
    goldDeep: token('--color-primary-dark', [0.54, 0.48, 0.19]),
    ember: token('--color-accent', [0.96, 0.62, 0.04]),
    emberHot: token('--color-accent-light', [0.99, 0.9, 0.54]),
    emberDeep: token('--color-accent-dark', [0.71, 0.33, 0.04]),
    star: themed('--bl-star', [0.9, 0.88, 0.82]),
    skyTop: themed('--bl-sky-top', [0.02, 0.02, 0.02]),
    skyMid: themed('--bl-canvas', [0.05, 0.05, 0.04]),
    skyLow: themed('--bl-sky-low', [0.12, 0.1, 0.06]),
    sun: themed('--bl-sun', [0.27, 0.24, 0.1], true),
    ridgeFar: themed('--bl-ridge-far', [0.1, 0.09, 0.06]),
    ridgeMid: themed('--bl-ridge-mid', [0.07, 0.07, 0.05]),
    ridgeNear: themed('--bl-ridge-near', [0.03, 0.03, 0.02]),
    fire: themed('--bl-fire', [0.96, 0.62, 0.04]),
  }
}
